import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { tournamentDayApi } from "@bw/shared/api";
import { connectBrowserTournamentLive } from "@bw/shared/hooks";
import type { Schema } from "@bw/shared/types/generated";
import { useEffect, useRef } from "react";

type MatchRef = Schema["TournamentMatchRef"];
type Inspector = Schema["TournamentOperatorMatchInspector"];

type Props = {
  match: MatchRef | null;
  onClose: () => void;
  onUnavailable: () => void;
  revision: number;
  tournamentId: string;
};

function label(value: string): string {
  return value.replaceAll("_", " ");
}

function displayTime(value: string, venueTimezone: string): string {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      month: "short",
      timeZone: venueTimezone,
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return "Time unavailable";
  }
}

function statusOf(reason: unknown): number | undefined {
  return reason && typeof reason === "object"
    ? Number((reason as { status?: unknown }).status)
    : undefined;
}

export function OperatorMatchInspector({
  match,
  onClose,
  onUnavailable,
  revision,
  tournamentId,
}: Props) {
  const inspectorRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const originFocusRef = useRef<HTMLElement | null>(null);
  const scoreWakeRevisionRef = useRef(0);
  const retainedInspectorRef = useRef<{
    selectionKey: string;
    value: Inspector;
  } | null>(null);
  const shouldFocusResultRef = useRef(false);
  const handledKey = useRef<string | null>(null);
  const selectionKey = match ? `${match.kind}:${match.match_id}` : null;
  const inspector = useQuery({
    // Match detail is a projection of the same monotonic operator revision as
    // the control-center snapshot. Keeping that revision in the key means an
    // SSE wake cannot leave a selected row showing stale readiness, execution,
    // or score facts after the canonical page has refreshed.
    queryKey: [
      "tournament-operator-match-inspector",
      tournamentId,
      revision,
      match?.kind,
      match?.match_id,
    ],
    queryFn: () =>
      tournamentDayApi.getTournamentOperatorMatchInspector(
        tournamentId,
        match!.kind,
        match!.match_id,
      ),
    enabled: Boolean(match && tournamentId),
    retry: false,
  });
  const retainedValue =
    inspector.data ??
    (retainedInspectorRef.current?.selectionKey === selectionKey
      ? retainedInspectorRef.current.value
      : null);
  const scoreMayChange = Boolean(
    retainedValue?.score_available &&
      ["warmup", "in_progress", "interval", "suspended"].includes(
        retainedValue.execution.state,
      ),
  );
  useEffect(() => {
    if (!selectionKey || !inspector.data) return;
    retainedInspectorRef.current = {
      selectionKey,
      value: inspector.data,
    };
  }, [inspector.data, selectionKey]);
  useEffect(() => {
    scoreWakeRevisionRef.current = 0;
  }, [selectionKey, tournamentId]);
  useEffect(() => {
    if (!match || !scoreMayChange) return;
    return connectBrowserTournamentLive({
      audience: "operator_score",
      credentials: "include",
      match: { kind: match.kind, matchId: match.match_id },
      onRevision: (nextRevision, reset) => {
        if (!reset && nextRevision <= scoreWakeRevisionRef.current) return;
        scoreWakeRevisionRef.current = nextRevision;
        void inspector.refetch();
      },
      revision: scoreWakeRevisionRef.current,
      tournamentId,
    });
  }, [inspector.refetch, match, scoreMayChange, tournamentId]);
  useEffect(() => {
    if (!selectionKey || typeof document === "undefined") return;
    originFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    shouldFocusResultRef.current = true;
  }, [selectionKey]);
  const restoreOriginFocus = () => {
    const origin = originFocusRef.current;
    window.setTimeout(() => origin?.focus(), 0);
  };
  const close = () => {
    onClose();
    restoreOriginFocus();
  };
  const retry = () => {
    shouldFocusResultRef.current = true;
    void inspector.refetch();
  };
  useEffect(() => {
    // A passive revision refresh must not later focus the inspector after a
    // failed initial load. Only opening a selection and an explicit Retry arm
    // the next successful result for focus.
    if (inspector.isError) shouldFocusResultRef.current = false;
  }, [inspector.isError]);
  useEffect(() => {
    if (
      !selectionKey ||
      !inspector.isError ||
      handledKey.current === selectionKey
    )
      return;
    if (![403, 404, 409, 422].includes(statusOf(inspector.error) ?? 0)) return;
    handledKey.current = selectionKey;
    const inspectorHadFocus = Boolean(
      inspectorRef.current?.contains(document.activeElement),
    );
    onUnavailable();
    if (inspectorHadFocus) restoreOriginFocus();
  }, [inspector.error, inspector.isError, onUnavailable, selectionKey]);
  useEffect(() => {
    if (
      !selectionKey ||
      !inspector.isSuccess ||
      !shouldFocusResultRef.current
    ) {
      return;
    }
    shouldFocusResultRef.current = false;
    headingRef.current?.focus();
  }, [inspector.isSuccess, selectionKey]);

  if (!match) return null;
  if (inspector.isError) {
    return (
      <Paper
        component="section"
        aria-label="Match inspector"
        ref={inspectorRef}
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Stack spacing={1}>
          <Alert role="alert" severity="error">
            Match details are temporarily unavailable.
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button onClick={retry} size="small">
              Retry
            </Button>
            <Button onClick={close} size="small">
              Close details
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }
  const value = retainedValue;
  if (!value) {
    return (
      <Paper
        component="section"
        aria-label="Match inspector"
        ref={inspectorRef}
        variant="outlined"
        sx={{ p: 2 }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography aria-live="polite">Loading selected match…</Typography>
          <Button onClick={close} size="small">
            Close details
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      aria-busy={inspector.isFetching}
      component="section"
      aria-label="Match inspector"
      ref={inspectorRef}
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Stack spacing={1.25}>
        <Typography component="h2" ref={headingRef} tabIndex={-1} variant="h6">
          {value.match_display_label}
        </Typography>
        {inspector.isFetching ? (
          <Typography aria-live="polite" color="text.secondary" variant="body2">
            Refreshing match details…
          </Typography>
        ) : null}
        <Typography color="text.secondary" variant="body2">
          {value.event_label} · {value.draw_label}
        </Typography>
        <Stack spacing={0.25}>
          <Typography variant="body2">{value.court_label}</Typography>
          <Typography color="text.secondary" variant="body2">
            Planned {displayTime(value.planned_start_at, value.venue_timezone)}{" "}
            – {displayTime(value.planned_end_at, value.venue_timezone)}
            {value.pinned ? " · pinned" : ""}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {label(value.execution.state)} ·{" "}
            {label(value.execution.dispatch_state)}
          </Typography>
        </Stack>
        <Divider />
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Sides</Typography>
          {value.participants.length ? (
            value.participants.map((participant) => (
              <Typography
                key={`${participant.side}:${participant.handle}`}
                variant="body2"
              >
                {participant.side === "side1" ? "Side 1" : "Side 2"}:{" "}
                <span>{participant.handle}</span>
              </Typography>
            ))
          ) : (
            <Typography color="text.secondary" variant="body2">
              Participants are not determined yet.
            </Typography>
          )}
        </Stack>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Readiness</Typography>
          {value.readiness.blockers.length ? (
            value.readiness.blockers.map((blocker) => (
              <Typography
                color="text.secondary"
                key={blocker.code}
                variant="body2"
              >
                {label(blocker.code)}
                {blocker.until
                  ? ` until ${displayTime(blocker.until, value.venue_timezone)}`
                  : ""}
              </Typography>
            ))
          ) : (
            <Typography color="text.secondary" variant="body2">
              Ready for the next permitted action.
            </Typography>
          )}
        </Stack>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Officials</Typography>
          <Typography color="text.secondary" variant="body2">
            {value.assigned_official_roles.length
              ? value.assigned_official_roles.map(label).join(", ")
              : "No match or court official is assigned."}
          </Typography>
        </Stack>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">Score</Typography>
          {value.score_available && value.score ? (
            <Typography color="text.secondary" variant="body2">
              {value.score.current_game.side1}–{value.score.current_game.side2}
            </Typography>
          ) : (
            <Typography color="text.secondary" variant="body2">
              Score unavailable for this assignment.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
